import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parse } from "date-fns";
import {
  Users, Search, Download, Upload, Pencil, Plus, IndianRupee,
  Mail, Loader2, CheckCircle2, X, FileSpreadsheet, ChevronRight,
  Landmark, CreditCard, Save, Trash2, Eye, EyeOff, Calculator,
  CalendarRange, FolderOpen, History, RotateCcw, Copy, Zap,
  StickyNote, MoreHorizontal, ArrowUpDown, Filter, Shield,
  ArrowDown, ArrowUp, ChevronLeft, ArrowRightLeft, Table2, RefreshCw,
  CalendarIcon,
} from "lucide-react";
import EntityHistory from "@/components/EntityHistory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaskSortHeader = ({ field, sortBy, sortOrder, onSort, children, align = "left" }) => (
  <th
    className={`px-2 py-2 text-${align} text-[10px] font-semibold text-slate-500 uppercase cursor-pointer hover:text-slate-800 transition-colors select-none whitespace-nowrap`}
    onClick={() => onSort(field)}
    data-testid={`rc-task-sort-${field}`}
  >
    <span className="inline-flex items-center gap-1">
      {children}
      {sortBy === field ? (
        <svg className={`w-3 h-3 transition-transform ${sortOrder === "asc" ? "" : "rotate-180"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
      ) : (
        <svg className="w-3 h-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
      )}
    </span>
  </th>
);

const ResourceCenter = () => {
  // ── State ──────────────────────────────────────────
  const [resources, setResources] = useState([]);
  const [allTaskTypes, setAllTaskTypes] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedResource, setSelectedResource] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [rateFilter, setRateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  // Profile edit
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Rates
  const [rates, setRates] = useState([]);
  const [ratesSaving, setRatesSaving] = useState(false);
  const [showAddRate, setShowAddRate] = useState(false);
  const [newRate, setNewRate] = useState({ task_type: "", language: "", rate: "", rate_type: "per_unit", from_date: "", to_date: "", project: "" });

  // Recalculate
  const [showRecalcDialog, setShowRecalcDialog] = useState(false);
  const [recalcRateIdx, setRecalcRateIdx] = useState(null);
  const [recalcForm, setRecalcForm] = useState({ from_date: "", to_date: "", projects: [] });
  const [resourceProjects, setResourceProjects] = useState([]);
  const [recalculating, setRecalculating] = useState(false);

  // Import
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Rate History
  const [rateHistory, setRateHistory] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [expandedHistoryItem, setExpandedHistoryItem] = useState(null);

  // Clone rates
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [cloneSource, setCloneSource] = useState("");
  const [cloning, setCloning] = useState(false);

  // Notes
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  // Apply all rates
  const [applyingAll, setApplyingAll] = useState(false);

  // Default rates
  const [showDefaultRates, setShowDefaultRates] = useState(false);
  const [defaultRates, setDefaultRates] = useState([]);
  const [defaultRatesLoading, setDefaultRatesLoading] = useState(false);
  const [defaultRatesSaving, setDefaultRatesSaving] = useState(false);
  const [newDefaultRate, setNewDefaultRate] = useState({ task_type: "", rate: "", rate_type: "per_unit" });

  // Global apply
  const [applyingGlobal, setApplyingGlobal] = useState(false);

  // ── Resource Tasks Table ───────────────────────────
  const [resourceTasks, setResourceTasks] = useState([]);
  const [tasksTotal, setTasksTotal] = useState(0);
  const [tasksPage, setTasksPage] = useState(1);
  const [tasksPages, setTasksPages] = useState(1);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksSearch, setTasksSearch] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [taskStatusFilter, setTaskStatusFilter] = useState("");
  const [taskMonthFilter, setTaskMonthFilter] = useState("");
  const [taskFromDate, setTaskFromDate] = useState("");
  const [taskToDate, setTaskToDate] = useState("");
  const [tasksSortBy, setTasksSortBy] = useState("due_date");
  const [tasksSortOrder, setTasksSortOrder] = useState("desc");
  const [taskFilters, setTaskFilters] = useState({ task_types: [], statuses: [], months: [] });

  // Task edit dialog
  const [showTaskEditDialog, setShowTaskEditDialog] = useState(false);
  const [taskEditTarget, setTaskEditTarget] = useState(null);
  const [taskEditForm, setTaskEditForm] = useState({});
  const [taskEditSaving, setTaskEditSaving] = useState(false);

  // Task audit trail
  const [showTaskAuditDialog, setShowTaskAuditDialog] = useState(false);
  const [taskAuditData, setTaskAuditData] = useState(null);
  const [taskAuditLoading, setTaskAuditLoading] = useState(false);

  // ── Fetch ──────────────────────────────────────────
  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/resource-center`);
      setResources(res.data.resources || []);
      setAllTaskTypes(res.data.all_task_types || []);
      setAllLanguages(res.data.all_languages || []);
    } catch { toast.error("Failed to load resources"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  const selectResource = async (r) => {
    setDetailLoading(true);
    setHistoryExpanded(false);
    try {
      const res = await axios.get(`${API}/resource-center/${encodeURIComponent(r.email)}`);
      setSelectedResource(res.data);
      setRates(res.data.task_rates || []);
      setEditForm({ display_name: res.data.display_name, personal_email: res.data.personal_email || "", phone_number: res.data.phone_number || "", status: res.data.status || "active", bank_name: res.data.bank_name || "", account_number: res.data.account_number || "", ifsc_code: res.data.ifsc_code || "", account_holder: res.data.account_holder || "", pan_number: res.data.pan_number || "", linked_translator_email: res.data.linked_translator?.email || "" });
      setNotesText(res.data.notes || "");
    } catch { toast.error("Failed to load resource"); }
    finally { setDetailLoading(false); setEditMode(false); }
  };

  // ── Actions ────────────────────────────────────────
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}`, editForm);
      toast.success("Profile saved");
      setEditMode(false);
      fetchResources();
      selectResource({ email: selectedResource.email });
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleSaveRates = async () => {
    setRatesSaving(true);
    try {
      await axios.put(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/rates`, { rates });
      // Auto-apply rates after save
      const applyRes = await axios.post(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/apply-all-rates`);
      const diff = applyRes.data.amount_change;
      toast.success(`Rates saved & applied: ${applyRes.data.updated} tasks updated${diff !== 0 ? ` (${diff >= 0 ? "+" : ""}₹${Math.abs(diff).toLocaleString("en-IN")})` : ""}`);
      fetchResources();
      selectResource({ email: selectedResource.email });
      if (historyExpanded) fetchRateHistory();
    } catch { toast.error("Save rates failed"); }
    finally { setRatesSaving(false); }
  };

  const addRate = () => {
    setRates(prev => [...prev, { task_type: newRate.task_type, language: newRate.language || null, rate: parseFloat(newRate.rate), rate_type: newRate.rate_type, from_date: newRate.from_date || null, to_date: newRate.to_date || null, project: newRate.project || null }]);
    setShowAddRate(false);
    setNewRate({ task_type: "", language: "", rate: "", rate_type: "per_unit", from_date: "", to_date: "", project: "" });
    toast.info("Rate added locally — click Save to persist");
  };

  const removeRate = (idx) => setRates(prev => prev.filter((_, i) => i !== idx));

  const openRecalcDialog = async (rateIdx) => {
    setRecalcRateIdx(rateIdx);
    setRecalcForm({ from_date: "", to_date: "", projects: [] });
    setShowRecalcDialog(true);
    try {
      const res = await axios.get(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/projects`);
      setResourceProjects(res.data.projects || []);
    } catch { setResourceProjects([]); }
  };

  const toggleProjectSelect = (name) => {
    setRecalcForm(p => ({ ...p, projects: p.projects.includes(name) ? p.projects.filter(x => x !== name) : [...p.projects, name] }));
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await axios.post(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/recalculate`, { rate_index: recalcRateIdx, ...recalcForm });
      toast.success(`Recalculated: ${res.data.recalculated} of ${res.data.total_scanned} tasks updated`);
      setShowRecalcDialog(false);
      fetchResources();
      selectResource({ email: selectedResource.email });
      if (historyExpanded) fetchRateHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Recalculation failed"); }
    finally { setRecalculating(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/resource-center/import/bulk`, formData);
      toast.success(`Import: ${res.data.created} created, ${res.data.updated} updated, ${res.data.skipped} skipped`);
      fetchResources();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleExport = (format) => window.open(`${API}/resource-center/export/${format}`, "_blank");

  // Rate history
  const fetchRateHistory = async () => {
    if (!selectedResource) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/rate-history`);
      setRateHistory(res.data.history || []);
    } catch { setRateHistory([]); }
    finally { setHistoryLoading(false); }
  };

  const toggleHistory = () => {
    if (!historyExpanded) fetchRateHistory();
    setHistoryExpanded(!historyExpanded);
  };

  const filteredHistory = rateHistory.filter(h => historyFilter === "all" || h.action === historyFilter);

  // Clone rates
  const handleCloneRates = async () => {
    if (!cloneSource) return;
    setCloning(true);
    try {
      const res = await axios.post(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/clone-rates`, { source_email: cloneSource });
      toast.success(`Cloned ${res.data.rates_cloned} rates from ${cloneSource}`);
      setShowCloneDialog(false);
      selectResource({ email: selectedResource.email });
      if (historyExpanded) fetchRateHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Clone failed"); }
    finally { setCloning(false); }
  };

  // Apply all rates
  const handleApplyAllRates = async () => {
    setApplyingAll(true);
    try {
      const res = await axios.post(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/apply-all-rates`);
      const diff = res.data.amount_change;
      toast.success(`Applied all rates: ${res.data.updated} of ${res.data.total_tasks} tasks updated${diff !== 0 ? ` (${diff >= 0 ? "+" : ""}₹${Math.abs(diff).toLocaleString("en-IN")})` : ""}`);
      fetchResources();
      selectResource({ email: selectedResource.email });
      if (historyExpanded) fetchRateHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Apply failed"); }
    finally { setApplyingAll(false); }
  };

  // Default rates
  const fetchDefaultRates = async () => {
    setDefaultRatesLoading(true);
    try {
      const res = await axios.get(`${API}/resource-center/default-rates/all`);
      setDefaultRates(res.data.rates || []);
    } catch {}
    finally { setDefaultRatesLoading(false); }
  };

  const handleSaveDefaultRates = async () => {
    setDefaultRatesSaving(true);
    try {
      await axios.put(`${API}/resource-center/default-rates/save`, { rates: defaultRates });
      toast.success("Default rates saved");
    } catch { toast.error("Failed to save default rates"); }
    finally { setDefaultRatesSaving(false); }
  };

  const addDefaultRate = () => {
    if (!newDefaultRate.task_type || !newDefaultRate.rate) return;
    setDefaultRates(prev => [...prev, { task_type: newDefaultRate.task_type, rate: parseFloat(newDefaultRate.rate), rate_type: newDefaultRate.rate_type }]);
    setNewDefaultRate({ task_type: "", rate: "", rate_type: "per_unit" });
  };

  const removeDefaultRate = (idx) => setDefaultRates(prev => prev.filter((_, i) => i !== idx));

  const handleApplyGlobal = async () => {
    if (!window.confirm("Apply rates to ALL resources across the database? Individual rates take priority over defaults.")) return;
    setApplyingGlobal(true);
    try {
      const res = await axios.post(`${API}/resource-center/apply-rates-global`);
      const diff = res.data.amount_change;
      toast.success(`Global rates applied: ${res.data.updated} tasks updated (${res.data.by_source.individual || 0} individual, ${res.data.by_source.default || 0} default)${diff !== 0 ? ` | ${diff >= 0 ? "+" : ""}₹${Math.abs(diff).toLocaleString("en-IN")}` : ""}`);
      fetchResources();
      if (selectedResource) selectResource({ email: selectedResource.email });
    } catch (err) { toast.error(err.response?.data?.detail || "Global apply failed"); }
    finally { setApplyingGlobal(false); }
  };

  // Notes
  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/notes`, { notes: notesText });
      toast.success("Notes saved");
      setShowNotesDialog(false);
    } catch { toast.error("Save notes failed"); }
    finally { setSavingNotes(false); }
  };

  // Delete resource
  const handleDeleteResource = async () => {
    if (!window.confirm(`Delete mapping for ${selectedResource.email}? This removes rates and profile but NOT task data.`)) return;
    try {
      await axios.delete(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}`);
      toast.success("Resource mapping deleted");
      setSelectedResource(null);
      fetchResources();
    } catch { toast.error("Delete failed"); }
  };

  // ── Resource Tasks ─────────────────────────────────
  const fetchResourceTasks = useCallback(async (pg = 1) => {
    if (!selectedResource) return;
    setTasksLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 50, sort_by: tasksSortBy, sort_order: tasksSortOrder });
      if (tasksSearch) params.set("search", tasksSearch);
      if (taskTypeFilter) params.set("task_type", taskTypeFilter);
      if (taskStatusFilter) params.set("task_status", taskStatusFilter);
      if (taskMonthFilter) params.set("month", taskMonthFilter);
      if (taskFromDate) params.set("from_date", taskFromDate);
      if (taskToDate) params.set("to_date", taskToDate);
      const res = await axios.get(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/tasks?${params}`);
      setResourceTasks(res.data.tasks || []);
      setTasksTotal(res.data.total || 0);
      setTasksPages(res.data.pages || 1);
      setTasksPage(pg);
      setTaskFilters(res.data.filters || { task_types: [], statuses: [], months: [] });
    } catch { toast.error("Failed to load tasks"); }
    finally { setTasksLoading(false); }
  }, [selectedResource, tasksSearch, taskTypeFilter, taskStatusFilter, taskMonthFilter, taskFromDate, taskToDate, tasksSortBy, tasksSortOrder]);

  // Fetch tasks when resource is selected or filters change
  useEffect(() => {
    if (selectedResource) {
      fetchResourceTasks(1);
    }
  }, [selectedResource?.email, tasksSearch, taskTypeFilter, taskStatusFilter, taskMonthFilter, taskFromDate, taskToDate, tasksSortBy, tasksSortOrder]);

  const toggleTaskSort = (field) => {
    if (tasksSortBy === field) setTasksSortOrder(p => p === "asc" ? "desc" : "asc");
    else { setTasksSortBy(field); setTasksSortOrder("desc"); }
  };

  const clearTaskFilters = () => { setTasksSearch(""); setTaskTypeFilter(""); setTaskStatusFilter(""); setTaskMonthFilter(""); setTaskFromDate(""); setTaskToDate(""); };
  const hasTaskFilters = tasksSearch || taskTypeFilter || taskStatusFilter || taskMonthFilter || taskFromDate || taskToDate;

  // Edit task
  const openTaskEdit = (task) => {
    setTaskEditTarget(task);
    setTaskEditForm({
      main_title: task.main_title || "", episode_title: task.episode_title || "",
      task: task.task || "", resource: task.resource || "", language: task.language || "",
      due_date: task.due_date || "", duration_words: task.duration_words || 0,
      amount: task.amount || 0, task_status: task.task_status || "",
    });
    setShowTaskEditDialog(true);
  };

  const handleTaskEditSave = async () => {
    if (!taskEditTarget) return;
    setTaskEditSaving(true);
    try {
      const res = await axios.put(`${API}/adjustments/record/${taskEditTarget.id}`, { fields: taskEditForm });
      toast.success(`Updated ${res.data.changed} field(s)`);
      setShowTaskEditDialog(false);
      fetchResourceTasks(tasksPage);
      fetchResources();
      selectResource({ email: selectedResource.email });
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    finally { setTaskEditSaving(false); }
  };

  // Audit trail
  const fetchTaskAudit = async (taskId) => {
    setTaskAuditLoading(true);
    try {
      const res = await axios.get(`${API}/adjustments/${taskId}/history`);
      setTaskAuditData(res.data);
      setShowTaskAuditDialog(true);
    } catch { toast.error("Failed to load history"); }
    finally { setTaskAuditLoading(false); }
  };

  // Single task delete
  const handleTaskDelete = async (taskId) => {
    if (!window.confirm("Delete this task record? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/adjustments/record/${taskId}`);
      toast.success("Record deleted");
      fetchResourceTasks(tasksPage);
      fetchResources();
      selectResource({ email: selectedResource.email });
    } catch { toast.error("Delete failed"); }
  };

  // ── Filtering & Sorting ────────────────────────────
  const filteredResources = resources
    .filter(r => {
      if (search) {
        const q = search.toLowerCase();
        if (!r.display_name?.toLowerCase().includes(q) && !r.email?.toLowerCase().includes(q)) return false;
      }
      if (statusFilter !== "all" && (r.status || "active") !== statusFilter) return false;
      if (rateFilter === "has_rates" && (!r.task_rates || r.task_rates.length === 0)) return false;
      if (rateFilter === "no_rates" && r.task_rates && r.task_rates.length > 0) return false;
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === "name") cmp = (a.display_name || a.email).localeCompare(b.display_name || b.email);
      else if (sortBy === "amount") cmp = (a.total_amount || 0) - (b.total_amount || 0);
      else if (sortBy === "tasks") cmp = (a.task_count || 0) - (b.task_count || 0);
      else if (sortBy === "rates") cmp = (a.task_rates?.length || 0) - (b.task_rates?.length || 0);
      return sortDir === "desc" ? -cmp : cmp;
    });

  const resourcesWithRates = resources.filter(r => r.task_rates?.length > 0);

  // ── Render ─────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden" data-testid="resource-center-page">
      {/* ── Left Panel: Resource List ── */}
      <div className="w-[280px] flex-shrink-0 border-r border-slate-200 flex flex-col bg-white">
        {/* Header */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Users className="w-5 h-5 text-teal-600" /> Resources</h1>
            <span className="text-xs text-slate-400 font-mono">{filteredResources.length}/{resources.length}</span>
          </div>
          {/* Search */}
          <div className="relative mb-2.5">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email..." className="pl-8 h-8 text-sm" data-testid="rc-search" />
          </div>

          {/* Filter row */}
          <div className="flex items-center gap-1.5 mb-2.5">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-7 text-[10px] flex-1" data-testid="rc-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={rateFilter} onValueChange={setRateFilter}>
              <SelectTrigger className="h-7 text-[10px] flex-1" data-testid="rc-filter-rates"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rates</SelectItem>
                <SelectItem value="has_rates">Has Rates</SelectItem>
                <SelectItem value="no_rates">No Rates</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort + Actions row */}
          <div className="flex items-center gap-1.5">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-7 text-[10px] flex-1" data-testid="rc-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Sort: Name</SelectItem>
                <SelectItem value="amount">Sort: Amount</SelectItem>
                <SelectItem value="tasks">Sort: Tasks</SelectItem>
                <SelectItem value="rates">Sort: Rates</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")} data-testid="rc-sort-dir">
              {sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
            </Button>
            <div className="flex items-center gap-0.5 ml-auto">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600" onClick={() => fileInputRef.current?.click()} disabled={importing} title="Import" data-testid="rc-import-btn">
                <Upload className="w-3.5 h-3.5" />
              </Button>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} className="hidden" />
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => handleExport("excel")} title="Export Excel" data-testid="rc-export-excel-btn">
                <FileSpreadsheet className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-600" onClick={() => handleExport("csv")} title="Export CSV" data-testid="rc-export-csv-btn">
                <Download className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Resource list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-16"><Users className="w-8 h-8 mx-auto mb-2 text-slate-200" /><p className="text-sm text-slate-400">No resources found</p></div>
          ) : (
            filteredResources.map(r => (
              <div
                key={r.email}
                onClick={() => selectResource(r)}
                className={`px-4 py-3 border-b border-slate-100 cursor-pointer transition-colors hover:bg-slate-50 ${selectedResource?.email === r.email ? "bg-teal-50 border-l-2 border-l-teal-500" : ""}`}
                data-testid={`rc-resource-${r.email}`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-semibold text-slate-800 truncate max-w-[180px]">{r.display_name}</span>
                  <div className="flex items-center gap-1">
                    {(r.status || "active") !== "active" && <Badge className="bg-red-50 text-red-600 border-0 text-[8px]">{r.status}</Badge>}
                    {r.task_rates?.length > 0 && <Badge className="bg-emerald-50 text-emerald-600 border-0 text-[8px]">{r.task_rates.length} rates</Badge>}
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 truncate mb-1">{r.email}</p>
                <div className="flex items-center gap-3 text-[10px] text-slate-500">
                  <span>{r.task_count} tasks</span>
                  <span className="font-mono font-medium text-slate-700">{"\u20B9"}{(r.total_amount || 0).toLocaleString("en-IN")}</span>
                  {r.notes && <StickyNote className="w-3 h-3 text-amber-400" />}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Bottom actions */}
        <div className="p-3 border-t border-slate-200 space-y-1.5">
          <Button variant="outline" size="sm" className="w-full h-8 text-[11px] text-indigo-600 border-indigo-200 hover:bg-indigo-50" onClick={() => { setShowDefaultRates(true); fetchDefaultRates(); }} data-testid="rc-default-rates-btn">
            <IndianRupee className="w-3.5 h-3.5 mr-1" />Default Task Rates
          </Button>
          <Button size="sm" className="w-full h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={handleApplyGlobal} disabled={applyingGlobal} data-testid="rc-apply-global-btn">
            {applyingGlobal ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
            {applyingGlobal ? "Applying..." : "Apply Rates to All"}
          </Button>
        </div>
      </div>

      {/* ── Right Panel: Resource Detail ── */}
      <div className="flex-1 overflow-y-auto bg-slate-50/50 p-4">
        {!selectedResource ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <Users className="w-12 h-12 mb-3 text-slate-200" />
            <p className="text-sm">Select a resource to view details</p>
            <p className="text-xs mt-1">{resources.length} total resources &middot; {resourcesWithRates.length} with rates</p>
          </div>
        ) : detailLoading ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            {/* ── Centralized Action Bar ── */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="rc-action-bar">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditMode(!editMode)} data-testid="rc-edit-profile-btn">
                <Pencil className="w-3.5 h-3.5 mr-1" /> {editMode ? "Cancel Edit" : "Edit Profile"}
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={async () => { setShowAddRate(true); try { const res = await axios.get(`${API}/resource-center/${encodeURIComponent(selectedResource.email)}/projects`); setResourceProjects(res.data.projects || []); } catch { setResourceProjects([]); } }} data-testid="rc-add-rate-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Rate
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowCloneDialog(true)} data-testid="rc-clone-rates-btn">
                <Copy className="w-3.5 h-3.5 mr-1" /> Clone Rates
              </Button>
              {rates.length > 0 && (
                <Button variant="outline" size="sm" className="h-8 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={handleApplyAllRates} disabled={applyingAll} data-testid="rc-apply-all-btn">
                  {applyingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                  Apply All Rates
                </Button>
              )}
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setNotesText(selectedResource.notes || ""); setShowNotesDialog(true); }} data-testid="rc-notes-btn">
                <StickyNote className="w-3.5 h-3.5 mr-1" /> Notes
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={toggleHistory} data-testid="rc-rate-history-btn">
                <History className="w-3.5 h-3.5 mr-1" /> {historyExpanded ? "Hide History" : "History"}
              </Button>
              <div className="ml-auto">
                <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500 hover:bg-red-50" onClick={handleDeleteResource} data-testid="rc-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            </div>

            {/* ── Profile Header ── */}
            <Card data-testid="rc-profile-card">
              <CardContent className="p-4">
                {!editMode ? (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {(selectedResource.display_name || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-bold text-slate-900">{selectedResource.display_name}</h2>
                      <p className="text-xs text-slate-400">{selectedResource.email}</p>
                      {selectedResource.personal_email && <p className="text-xs text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" /> {selectedResource.personal_email}</p>}
                      {selectedResource.notes && <p className="text-xs text-amber-600 mt-1 italic flex items-center gap-1"><StickyNote className="w-3 h-3" /> {selectedResource.notes}</p>}
                      {selectedResource.linked_translator && <p className="text-xs text-blue-600 mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> Linked to: {selectedResource.linked_translator.name} ({selectedResource.linked_translator.email})</p>}
                      {selectedResource.phone_number && <p className="text-xs text-slate-500 mt-1">{selectedResource.phone_number}</p>}
                    </div>
                    <Badge className={`border-0 text-[10px] ${(selectedResource.status || "active") === "active" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{selectedResource.status || "active"}</Badge>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Display Name</label>
                        <Input value={editForm.display_name || ""} onChange={e => setEditForm(p => ({...p, display_name: e.target.value}))} className="h-8 text-sm" data-testid="rc-edit-name" /></div>
                      <div><label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Personal Email</label>
                        <Input value={editForm.personal_email || ""} onChange={e => setEditForm(p => ({...p, personal_email: e.target.value}))} className="h-8 text-sm" data-testid="rc-edit-personal-email" /></div>
                      <div><label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Phone (WhatsApp)</label>
                        <Input value={editForm.phone_number || ""} onChange={e => setEditForm(p => ({...p, phone_number: e.target.value}))} placeholder="+91..." className="h-8 text-sm" data-testid="rc-edit-phone" /></div>
                      <div><label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Status</label>
                        <Select value={editForm.status || "active"} onValueChange={v => setEditForm(p => ({...p, status: v}))}>
                          <SelectTrigger className="h-8 text-sm" data-testid="rc-edit-status"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="on_hold">On Hold</SelectItem></SelectContent>
                        </Select></div>
                      <div className="col-span-2 bg-blue-50 rounded-lg p-3">
                        <label className="text-[10px] font-medium text-blue-700 uppercase mb-1 block">Linked Translator Account</label>
                        <div className="flex items-center gap-2">
                          <Input value={editForm.linked_translator_email || ""} onChange={e => setEditForm(p => ({...p, linked_translator_email: e.target.value}))} placeholder="Translator's personal login email" className="h-8 text-sm flex-1" data-testid="rc-edit-linked-translator" />
                          {selectedResource?.linked_translator && (
                            <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px] shrink-0">Current: {selectedResource.linked_translator.name} ({selectedResource.linked_translator.email})</Badge>
                          )}
                        </div>
                        <p className="text-[9px] text-blue-500 mt-1">This links the translator's portal to see tasks assigned to {selectedResource?.email}</p>
                      </div>
                      <div><label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">PAN Number</label>
                        <Input value={editForm.pan_number || ""} onChange={e => setEditForm(p => ({...p, pan_number: e.target.value}))} className="h-8 text-sm" data-testid="rc-edit-pan" /></div>
                      <div><label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Bank Name</label>
                        <Input value={editForm.bank_name || ""} onChange={e => setEditForm(p => ({...p, bank_name: e.target.value}))} className="h-8 text-sm" data-testid="rc-edit-bank" /></div>
                      <div><label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Account Number</label>
                        <Input value={editForm.account_number || ""} onChange={e => setEditForm(p => ({...p, account_number: e.target.value}))} className="h-8 text-sm" data-testid="rc-edit-account" /></div>
                      <div><label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">IFSC Code</label>
                        <Input value={editForm.ifsc_code || ""} onChange={e => setEditForm(p => ({...p, ifsc_code: e.target.value}))} className="h-8 text-sm" data-testid="rc-edit-ifsc" /></div>
                      <div><label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Account Holder</label>
                        <Input value={editForm.account_holder || ""} onChange={e => setEditForm(p => ({...p, account_holder: e.target.value}))} className="h-8 text-sm" data-testid="rc-edit-holder" /></div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSaveProfile} disabled={saving} size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="rc-save-profile-btn">
                        {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save Profile
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── Stats Row ── */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "Tasks", value: selectedResource.task_count || 0, icon: <FileSpreadsheet className="w-4 h-4 text-blue-600" />, color: "blue" },
                { label: "Total Amount", value: `₹${(selectedResource.total_amount || 0).toLocaleString("en-IN")}`, icon: <IndianRupee className="w-4 h-4 text-emerald-600" />, color: "emerald" },
                { label: "Languages", value: (selectedResource.languages || []).length, icon: <Users className="w-4 h-4 text-violet-600" />, color: "violet" },
                { label: "Task Types", value: (selectedResource.task_types || []).length, icon: <Landmark className="w-4 h-4 text-amber-600" />, color: "amber" },
              ].map(s => (
                <Card key={s.label} className="bg-white">
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg bg-${s.color}-50 flex items-center justify-center flex-shrink-0`}>{s.icon}</div>
                    <div><p className="text-lg font-bold text-slate-900">{s.value}</p><p className="text-[10px] text-slate-400">{s.label}</p></div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* ── Rate Mapping ── */}
            <Card data-testid="rc-rate-mapping">
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><IndianRupee className="w-4 h-4 text-emerald-600" /> Rate Mapping ({rates.length})</CardTitle>
                  <Button size="sm" className="h-7 text-[10px]" onClick={handleSaveRates} disabled={ratesSaving} data-testid="rc-save-rates-btn">
                    {ratesSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                    Save Rates
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {rates.length === 0 ? (
                  <div className="text-center py-6 text-slate-400">
                    <IndianRupee className="w-6 h-6 mx-auto mb-1 text-slate-200" />
                    <p className="text-xs">No rates configured</p>
                    <p className="text-[10px]">Use "Add Rate" or "Clone Rates" from the action bar</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {rates.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 group" data-testid={`rc-rate-row-${i}`}>
                        <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{r.task_type}</Badge>
                        {r.language ? <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]">{r.language}</Badge> : <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px]">All Langs</Badge>}
                        {r.project && <Badge className="bg-teal-100 text-teal-700 border-0 text-[10px]">{r.project}</Badge>}
                        <span className="ml-auto font-mono text-sm font-bold text-emerald-700">{"\u20B9"}{r.rate}</span>
                        <Badge className={`text-[9px] border-0 ${r.rate_type === "flat" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{r.rate_type}</Badge>
                        {(r.from_date || r.to_date) ? (
                          <span className="text-[9px] text-indigo-500"><CalendarRange className="w-3 h-3 inline mr-0.5" />{r.from_date || "..."} — {r.to_date || "ongoing"}</span>
                        ) : (
                          <span className="text-[9px] text-slate-300">No date limit</span>
                        )}
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-emerald-600" onClick={() => openRecalcDialog(i)} title="Recalculate"><Calculator className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeRate(i)} title="Remove"><X className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── Rate History Timeline (inline) ── */}
            {historyExpanded && (
              <Card data-testid="rc-rate-history-section">
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4 text-blue-600" /> Rate Change Timeline</CardTitle>
                    <div className="flex items-center gap-1">
                      {["all", "rates_updated", "recalculation"].map(f => (
                        <button key={f} onClick={() => setHistoryFilter(f)} className={`px-2 py-1 rounded-md text-[9px] font-medium transition-colors ${historyFilter === f ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} data-testid={`history-filter-${f}`}>
                          {f === "all" ? "All" : f === "rates_updated" ? "Rate Changes" : "Recalculations"}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                  ) : filteredHistory.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <History className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                      <p className="text-sm">{rateHistory.length === 0 ? "No history yet" : "No matching events"}</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-[15px] top-4 bottom-4 w-[2px] bg-gradient-to-b from-blue-200 via-slate-200 to-transparent" />
                      <div className="space-y-3">
                        {filteredHistory.map((h, i) => {
                          const isRecalc = h.action === "recalculation";
                          const isExpanded = expandedHistoryItem === (h.id || i);
                          const dateStr = h.timestamp ? new Date(h.timestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
                          return (
                            <div key={h.id || i} className="relative pl-10" data-testid={`rate-history-item-${i}`}>
                              <div className={`absolute left-[7px] top-3 w-[18px] h-[18px] rounded-full flex items-center justify-center ${isRecalc ? "bg-emerald-500 ring-4 ring-emerald-50" : "bg-blue-500 ring-4 ring-blue-50"}`}>
                                {isRecalc ? <RotateCcw className="w-2.5 h-2.5 text-white" /> : <Pencil className="w-2.5 h-2.5 text-white" />}
                              </div>
                              <div className={`rounded-lg border cursor-pointer transition-all hover:shadow-sm ${isRecalc ? "border-emerald-200 bg-emerald-50/30" : "border-blue-200 bg-blue-50/20"}`} onClick={() => setExpandedHistoryItem(isExpanded ? null : (h.id || i))}>
                                <div className="flex items-center justify-between px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <Badge className={`border-0 text-[9px] font-semibold ${isRecalc ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{isRecalc ? "Recalculation" : "Rate Update"}</Badge>
                                    {isRecalc && h.details?.tasks_updated && <span className="text-[10px] text-emerald-600 font-medium">{h.details.tasks_updated} tasks</span>}
                                    {!isRecalc && h.summary && <span className="text-[10px] text-slate-500">{h.summary.length} change{h.summary.length !== 1 ? "s" : ""}</span>}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {isRecalc && h.details?.amount_diff != null && <span className={`text-[10px] font-mono font-bold ${h.details.amount_diff >= 0 ? "text-emerald-600" : "text-red-500"}`}>{h.details.amount_diff >= 0 ? "+" : ""}{"\u20B9"}{Math.abs(h.details.amount_diff).toLocaleString("en-IN")}</span>}
                                    <span className="text-[9px] text-slate-400">{dateStr}</span>
                                    <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                                  </div>
                                </div>
                                {isExpanded && (
                                  <div className="px-3 pb-3 pt-0">
                                    <div className="border-t border-slate-200/60 pt-2.5">
                                      {!isRecalc && h.summary && <div className="space-y-1.5">{h.summary.map((s, j) => {
                                        const isAdded = s.startsWith("Added"); const isRemoved = s.startsWith("Removed");
                                        return (<div key={j} className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[11px] ${isAdded ? "bg-emerald-50 border border-emerald-100" : isRemoved ? "bg-red-50 border border-red-100" : "bg-amber-50 border border-amber-100"}`}>
                                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isAdded ? "bg-emerald-500" : isRemoved ? "bg-red-500" : "bg-amber-500"}`} />
                                          <span className={`font-medium ${isAdded ? "text-emerald-700" : isRemoved ? "text-red-700" : "text-amber-700"}`}>{isAdded ? "+" : isRemoved ? "-" : "~"}</span>
                                          <span className="text-slate-700">{s}</span>
                                        </div>);
                                      })}</div>}
                                      {isRecalc && h.details && <div className="space-y-2">
                                        <div className="grid grid-cols-3 gap-2">
                                          <div className="bg-white rounded-lg border border-slate-200 p-2 text-center"><p className="text-xs font-bold text-slate-800">{h.details.tasks_updated || 0}</p><p className="text-[8px] text-slate-400">Updated</p></div>
                                          <div className="bg-white rounded-lg border border-slate-200 p-2 text-center"><p className="text-xs font-bold text-slate-500">{h.details.tasks_scanned || 0}</p><p className="text-[8px] text-slate-400">Scanned</p></div>
                                          <div className="bg-white rounded-lg border border-slate-200 p-2 text-center"><p className={`text-xs font-bold font-mono ${(h.details.amount_diff || 0) >= 0 ? "text-emerald-600" : "text-red-500"}`}>{(h.details.amount_diff || 0) >= 0 ? "+" : ""}{"\u20B9"}{Math.abs(h.details.amount_diff || 0).toLocaleString("en-IN")}</p><p className="text-[8px] text-slate-400">Net Impact</p></div>
                                        </div>
                                        <div className="bg-white rounded-lg border border-slate-200 p-2.5 space-y-1 text-[10px]">
                                          <div className="flex justify-between"><span className="text-slate-500">Rate</span><span className="text-slate-700 font-medium">{h.details.rate_applied}</span></div>
                                          <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="text-slate-700">{h.details.date_range}</span></div>
                                          <div className="flex justify-between"><span className="text-slate-500">Projects</span><span className="text-slate-700 text-right max-w-[200px] truncate">{Array.isArray(h.details.projects) ? h.details.projects.join(", ") : h.details.projects}</span></div>
                                        </div>
                                      </div>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ── Audit History (delete/edit/revert events on this resource) ── */}
            <Card data-testid="rc-audit-history">
              <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4 text-violet-600" /> Audit History</CardTitle></CardHeader>
              <CardContent className="p-0">
                <EntityHistory collection="resource_mappings" entityId={selectedResource.email} compact onChanged={() => selectResource({ email: selectedResource.email })} />
              </CardContent>
            </Card>

            {/* ── Languages & Task Types ── */}
            {(selectedResource.languages?.length > 0 || selectedResource.task_types?.length > 0) && (
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="py-2"><CardTitle className="text-xs text-slate-500">Languages ({(selectedResource.languages || []).length})</CardTitle></CardHeader>
                  <CardContent className="pb-3"><div className="flex flex-wrap gap-1">{(selectedResource.languages || []).map(lg => <Badge key={lg} className="bg-violet-50 text-violet-700 border-0 text-[10px]">{lg}</Badge>)}</div></CardContent>
                </Card>
                <Card>
                  <CardHeader className="py-2"><CardTitle className="text-xs text-slate-500">Task Types ({(selectedResource.task_types || []).length})</CardTitle></CardHeader>
                  <CardContent className="pb-3"><div className="flex flex-wrap gap-1">{(selectedResource.task_types || []).map(t => <Badge key={t} className="bg-blue-50 text-blue-700 border-0 text-[10px]">{t}</Badge>)}</div></CardContent>
                </Card>
              </div>
            )}

            {/* ══ RESOURCE TASKS TABLE ══ */}
            <Card data-testid="rc-tasks-table-card">
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Table2 className="w-4 h-4 text-blue-600" /> Tasks ({tasksTotal})
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => fetchResourceTasks(tasksPage)} data-testid="rc-tasks-refresh">
                      <RefreshCw className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Task Filters */}
                <div className="flex items-center gap-2 flex-wrap px-4 pb-3">
                  <div className="relative flex-1 min-w-[150px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                    <Input value={tasksSearch} onChange={e => setTasksSearch(e.target.value)} placeholder="Search project, episode..." className="pl-7 h-7 text-[11px]" data-testid="rc-tasks-search" />
                  </div>
                  <Select value={taskTypeFilter || "all"} onValueChange={v => setTaskTypeFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="h-7 text-[10px] w-28" data-testid="rc-tasks-type-filter"><SelectValue placeholder="All Tasks" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tasks</SelectItem>
                      {taskFilters.task_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={taskStatusFilter || "all"} onValueChange={v => setTaskStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="h-7 text-[10px] w-28" data-testid="rc-tasks-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {taskFilters.statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={taskMonthFilter || "all"} onValueChange={v => setTaskMonthFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="h-7 text-[10px] w-28" data-testid="rc-tasks-month-filter"><SelectValue placeholder="All Months" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Months</SelectItem>
                      {taskFilters.months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className={`h-7 text-[10px] w-32 justify-start font-normal ${taskFromDate ? "text-slate-700" : "text-slate-400"}`} data-testid="rc-tasks-from-date">
                        <CalendarIcon className="w-3 h-3 mr-1.5 text-slate-400" />
                        {taskFromDate ? format(new Date(taskFromDate + "T00:00:00"), "dd MMM yyyy") : "From date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={taskFromDate ? new Date(taskFromDate + "T00:00:00") : undefined}
                        onSelect={(date) => setTaskFromDate(date ? format(date, "yyyy-MM-dd") : "")}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className={`h-7 text-[10px] w-32 justify-start font-normal ${taskToDate ? "text-slate-700" : "text-slate-400"}`} data-testid="rc-tasks-to-date">
                        <CalendarIcon className="w-3 h-3 mr-1.5 text-slate-400" />
                        {taskToDate ? format(new Date(taskToDate + "T00:00:00"), "dd MMM yyyy") : "To date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={taskToDate ? new Date(taskToDate + "T00:00:00") : undefined}
                        onSelect={(date) => setTaskToDate(date ? format(date, "yyyy-MM-dd") : "")}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {hasTaskFilters && (
                    <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-500 px-1.5" onClick={clearTaskFilters} data-testid="rc-tasks-clear-filters">
                      <X className="w-3 h-3 mr-0.5" /> Clear
                    </Button>
                  )}
                </div>

                {/* Task Table */}
                {tasksLoading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : resourceTasks.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                    <p className="text-xs">{hasTaskFilters ? "No tasks match filters" : "No tasks assigned"}</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="rc-tasks-table">
                        <thead className="bg-slate-50 border-y border-slate-200">
                          <tr>
                            <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500">#</th>
                            <TaskSortHeader field="main_title" sortBy={tasksSortBy} sortOrder={tasksSortOrder} onSort={toggleTaskSort}>Project</TaskSortHeader>
                            <TaskSortHeader field="episode_title" sortBy={tasksSortBy} sortOrder={tasksSortOrder} onSort={toggleTaskSort}>Episode</TaskSortHeader>
                            <TaskSortHeader field="task" sortBy={tasksSortBy} sortOrder={tasksSortOrder} onSort={toggleTaskSort} align="center">Task</TaskSortHeader>
                            <TaskSortHeader field="language" sortBy={tasksSortBy} sortOrder={tasksSortOrder} onSort={toggleTaskSort} align="center">Lang</TaskSortHeader>
                            <TaskSortHeader field="due_date" sortBy={tasksSortBy} sortOrder={tasksSortOrder} onSort={toggleTaskSort} align="center">Due Date</TaskSortHeader>
                            <TaskSortHeader field="duration_words" sortBy={tasksSortBy} sortOrder={tasksSortOrder} onSort={toggleTaskSort} align="center">Dur</TaskSortHeader>
                            <TaskSortHeader field="amount" sortBy={tasksSortBy} sortOrder={tasksSortOrder} onSort={toggleTaskSort} align="right">Amount</TaskSortHeader>
                            <TaskSortHeader field="task_status" sortBy={tasksSortBy} sortOrder={tasksSortOrder} onSort={toggleTaskSort} align="center">Status</TaskSortHeader>
                            <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resourceTasks.map((t, idx) => {
                            const edits = (t.adjustment_log || []).length;
                            return (
                              <tr key={t.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`rc-task-row-${idx}`}>
                                <td className="px-2 py-1.5 text-[10px] text-slate-400">{(tasksPage - 1) * 50 + idx + 1}</td>
                                <td className="px-2 py-1.5 text-[11px] font-medium text-slate-700 max-w-[200px] truncate" title={t.main_title}>{t.main_title || "-"}</td>
                                <td className="px-2 py-1.5 text-[11px] text-slate-600 max-w-[180px] truncate" title={t.episode_title}>{t.episode_title || "-"}</td>
                                <td className="px-2 py-1.5 text-center"><Badge className="bg-blue-50 text-blue-700 border-0 text-[9px]">{t.task || "-"}</Badge></td>
                                <td className="px-2 py-1.5 text-center text-[10px] text-slate-600">{t.language || "-"}</td>
                                <td className="px-2 py-1.5 text-center text-[10px] text-slate-500 whitespace-nowrap">{t.due_date || "-"}</td>
                                <td className="px-2 py-1.5 text-center text-[11px] text-slate-600">{t.duration_words || 0}</td>
                                <td className="px-2 py-1.5 text-right text-[11px] font-semibold text-slate-700">{"\u20B9"}{(t.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                                <td className="px-2 py-1.5 text-center">
                                  <Badge className={`border-0 text-[9px] ${t.task_status?.toLowerCase() === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                                    {t.task_status || "In Progress"}
                                  </Badge>
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="flex items-center justify-center gap-0.5">
                                    <button onClick={() => openTaskEdit(t)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-blue-600 transition-colors" title="Edit" data-testid={`rc-task-edit-${idx}`}><Pencil className="w-3 h-3" /></button>
                                    <button onClick={() => fetchTaskAudit(t.id)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-amber-600 transition-colors relative" title="History" data-testid={`rc-task-audit-${idx}`}>
                                      <History className="w-3 h-3" />
                                      {edits > 0 && <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[7px] rounded-full w-3 h-3 flex items-center justify-center leading-none">{edits}</span>}
                                    </button>
                                    <button onClick={() => handleTaskDelete(t.id)} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors" title="Delete" data-testid={`rc-task-delete-${idx}`}><Trash2 className="w-3 h-3" /></button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200">
                      <p className="text-[10px] text-slate-500">Showing {(tasksPage - 1) * 50 + 1}–{Math.min(tasksPage * 50, tasksTotal)} of {tasksTotal}</p>
                      <div className="flex items-center gap-1.5">
                        <Button variant="outline" size="sm" className="h-6 px-2" disabled={tasksPage <= 1} onClick={() => fetchResourceTasks(tasksPage - 1)} data-testid="rc-tasks-prev-page"><ChevronLeft className="w-3 h-3" /></Button>
                        <span className="text-[10px] text-slate-600">{tasksPage}/{tasksPages}</span>
                        <Button variant="outline" size="sm" className="h-6 px-2" disabled={tasksPage >= tasksPages} onClick={() => fetchResourceTasks(tasksPage + 1)} data-testid="rc-tasks-next-page"><ChevronRight className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* ══ DIALOGS ══ */}

      {/* ── Add Rate Dialog ── */}
      <Dialog open={showAddRate} onOpenChange={setShowAddRate}>
        <DialogContent className="max-w-md" data-testid="add-rate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-emerald-600" /> Add Rate Period</DialogTitle>
            <DialogDescription>Set rate for task type + language with optional date range</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Task Type</label>
              <Select value={newRate.task_type} onValueChange={v => setNewRate(p => ({ ...p, task_type: v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="add-rate-task-type"><SelectValue placeholder="Select task type..." /></SelectTrigger>
                <SelectContent>{allTaskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Language (blank = all languages)</label>
              <Select value={newRate.language || "all"} onValueChange={v => setNewRate(p => ({ ...p, language: v === "all" ? "" : v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="add-rate-language"><SelectValue placeholder="All Languages" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages (default)</SelectItem>
                  {allLanguages.map(lg => <SelectItem key={lg} value={lg}>{lg}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block flex items-center gap-1"><FolderOpen className="w-3 h-3" /> Project (blank = all projects)</label>
              <Select value={newRate.project || "all"} onValueChange={v => setNewRate(p => ({ ...p, project: v === "all" ? "" : v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="add-rate-project"><SelectValue placeholder="All Projects" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects (default)</SelectItem>
                  {resourceProjects.map(p => <SelectItem key={p.name} value={p.name}>{p.name} ({p.task_count} tasks)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Rate ({"\u20B9"})</label>
                <Input type="number" value={newRate.rate} onChange={e => setNewRate(p => ({ ...p, rate: e.target.value }))} className="h-8 text-sm" placeholder="0.00" data-testid="add-rate-amount" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Type</label>
                <Select value={newRate.rate_type} onValueChange={v => setNewRate(p => ({ ...p, rate_type: v }))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="add-rate-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="per_unit">Per Unit</SelectItem><SelectItem value="flat">Flat</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <hr className="border-slate-200" />
            <p className="text-[10px] font-semibold text-slate-500 uppercase flex items-center gap-1"><CalendarRange className="w-3 h-3" /> Validity Period (optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] font-medium text-slate-500 mb-1 block">From Date</label><Input type="date" value={newRate.from_date} onChange={e => setNewRate(p => ({ ...p, from_date: e.target.value }))} className="h-8 text-sm" data-testid="add-rate-from-date" /></div>
              <div><label className="text-[10px] font-medium text-slate-500 mb-1 block">To Date (blank = ongoing)</label><Input type="date" value={newRate.to_date} onChange={e => setNewRate(p => ({ ...p, to_date: e.target.value }))} className="h-8 text-sm" data-testid="add-rate-to-date" /></div>
            </div>
            <Button onClick={addRate} disabled={!newRate.task_type || !newRate.rate} className="w-full" data-testid="confirm-add-rate-btn"><Plus className="w-4 h-4 mr-1" /> Add Rate</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Recalculate Dialog ── */}
      <Dialog open={showRecalcDialog} onOpenChange={setShowRecalcDialog}>
        <DialogContent className="max-w-lg" data-testid="recalc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-emerald-600" /> Apply Rate & Recalculate</DialogTitle>
            <DialogDescription>Recalculate task amounts using the selected rate.</DialogDescription>
          </DialogHeader>
          {recalcRateIdx !== null && rates[recalcRateIdx] && (
            <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 text-sm">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-700 border-0">{rates[recalcRateIdx].task_type}</Badge>
                {rates[recalcRateIdx].language ? <Badge className="bg-violet-100 text-violet-700 border-0">{rates[recalcRateIdx].language}</Badge> : <Badge className="bg-slate-100 text-slate-500 border-0">All Langs</Badge>}
                <span className="ml-auto font-mono font-bold text-emerald-700">{"\u20B9"}{rates[recalcRateIdx].rate} / {rates[recalcRateIdx].rate_type}</span>
              </div>
            </div>
          )}
          <div className="space-y-3 pt-1">
            <p className="text-[10px] font-semibold text-slate-500 uppercase flex items-center gap-1"><CalendarRange className="w-3 h-3" /> Date Scope</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] font-medium text-slate-500 mb-1 block">From Date</label><Input type="date" value={recalcForm.from_date} onChange={e => setRecalcForm(p => ({ ...p, from_date: e.target.value }))} className="h-8 text-sm" data-testid="recalc-from-date" /></div>
              <div><label className="text-[10px] font-medium text-slate-500 mb-1 block">To Date</label><Input type="date" value={recalcForm.to_date} onChange={e => setRecalcForm(p => ({ ...p, to_date: e.target.value }))} className="h-8 text-sm" data-testid="recalc-to-date" /></div>
            </div>
            <p className="text-[10px] font-semibold text-slate-500 uppercase flex items-center gap-1"><FolderOpen className="w-3 h-3" /> Project Scope (optional)</p>
            <Input placeholder="Search projects..." className="h-7 text-xs mb-1" onChange={e => {
              const q = e.target.value.toLowerCase();
              document.querySelectorAll('[data-project-item]').forEach(el => {
                el.style.display = el.dataset.projectItem.toLowerCase().includes(q) ? '' : 'none';
              });
            }} data-testid="recalc-project-search" />
            <div className="max-h-[180px] overflow-y-auto border border-slate-200 rounded-lg">
              {resourceProjects.length === 0 ? <p className="text-xs text-slate-400 text-center py-4">No projects</p> : resourceProjects.map(p => (
                <label key={p.name} data-project-item={p.name} className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 ${recalcForm.projects.includes(p.name) ? "bg-indigo-50" : ""}`}>
                  <input type="checkbox" checked={recalcForm.projects.includes(p.name)} onChange={() => toggleProjectSelect(p.name)} className="rounded border-slate-300" />
                  <span className="text-xs text-slate-700 flex-1 truncate">{p.name}</span>
                  <span className="text-[10px] text-slate-400">{p.task_count} tasks</span>
                </label>
              ))}
            </div>
            {recalcForm.projects.length > 0 && <p className="text-[10px] text-indigo-600">{recalcForm.projects.length} project(s) selected</p>}
            <Button onClick={handleRecalculate} disabled={recalculating} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-recalc-btn">
              {recalculating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Calculator className="w-4 h-4 mr-1" />}
              {recalculating ? "Recalculating..." : "Apply Rate & Recalculate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Clone Rates Dialog ── */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent className="max-w-sm" data-testid="clone-rates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="w-5 h-5 text-indigo-600" /> Clone Rates</DialogTitle>
            <DialogDescription>Copy all rates from another resource to {selectedResource?.display_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Select value={cloneSource} onValueChange={setCloneSource}>
              <SelectTrigger className="h-9 text-sm" data-testid="clone-source-select"><SelectValue placeholder="Select source resource..." /></SelectTrigger>
              <SelectContent>
                {resourcesWithRates.filter(r => r.email !== selectedResource?.email).map(r => (
                  <SelectItem key={r.email} value={r.email}>{r.display_name} ({r.task_rates?.length} rates)</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleCloneRates} disabled={!cloneSource || cloning} className="w-full" data-testid="confirm-clone-btn">
              {cloning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />}
              {cloning ? "Cloning..." : "Clone Rates"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Notes Dialog ── */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="max-w-sm" data-testid="notes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><StickyNote className="w-5 h-5 text-amber-600" /> Notes</DialogTitle>
            <DialogDescription>Admin notes for {selectedResource?.display_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Textarea value={notesText} onChange={e => setNotesText(e.target.value)} placeholder="Add notes about this resource..." rows={4} className="text-sm" data-testid="notes-textarea" />
            <Button onClick={handleSaveNotes} disabled={savingNotes} className="w-full" data-testid="save-notes-btn">
              {savingNotes ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {savingNotes ? "Saving..." : "Save Notes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Task Edit Dialog ── */}
      <Dialog open={showTaskEditDialog} onOpenChange={setShowTaskEditDialog}>
        <DialogContent className="max-w-md" data-testid="rc-task-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-blue-600" /> Edit Task</DialogTitle>
            <DialogDescription>Changes are tracked in the audit trail and reflected in Database Center</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2 max-h-[400px] overflow-y-auto">
            {[
              { key: "main_title", label: "Project" }, { key: "episode_title", label: "Episode" },
              { key: "task", label: "Task Type" }, { key: "resource", label: "Resource" },
              { key: "language", label: "Language" }, { key: "due_date", label: "Due Date" },
              { key: "duration_words", label: "Duration/Words", type: "number" },
              { key: "amount", label: "Amount", type: "number" }, { key: "task_status", label: "Status" },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">{f.label}</label>
                <Input
                  type={f.type || "text"}
                  value={taskEditForm[f.key] ?? ""}
                  onChange={e => setTaskEditForm(prev => ({ ...prev, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value }))}
                  className="h-8 text-sm"
                  data-testid={`rc-task-edit-field-${f.key}`}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleTaskEditSave} disabled={taskEditSaving} className="flex-1" data-testid="rc-task-edit-save-btn">
              {taskEditSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {taskEditSaving ? "Saving..." : "Save Changes"}
            </Button>
            <Button variant="outline" onClick={() => setShowTaskEditDialog(false)} className="flex-1">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Task Audit Trail Dialog ── */}
      <Dialog open={showTaskAuditDialog} onOpenChange={setShowTaskAuditDialog}>
        <DialogContent className="max-w-lg" data-testid="rc-task-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-amber-600" /> Revision History</DialogTitle>
            {taskAuditData && <DialogDescription>{taskAuditData.main_title}{taskAuditData.episode_title ? ` — ${taskAuditData.episode_title}` : ""}</DialogDescription>}
          </DialogHeader>
          {taskAuditData && (
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Resource:</span><span className="font-medium text-slate-700">{taskAuditData.resource || "-"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Task Type:</span><span className="font-medium text-slate-700">{taskAuditData.task || "-"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Due Date:</span><span className="font-medium text-slate-700">{taskAuditData.due_date || "-"}</span></div>
            </div>
          )}
          <div className="space-y-2 pt-1 max-h-[350px] overflow-y-auto">
            {taskAuditLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : taskAuditData?.adjustment_log?.length > 0 ? (
              [...taskAuditData.adjustment_log].reverse().map((entry, i) => (
                <div key={i} className="p-3 bg-white rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-1.5">
                    <Badge className={`border-0 text-[10px] ${entry.field === "resource" ? "bg-blue-50 text-blue-700" : entry.field === "task" ? "bg-purple-50 text-purple-700" : entry.field === "amount" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {entry.action?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || entry.field}
                    </Badge>
                    <span className="text-[10px] text-slate-400">{new Date(entry.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-red-500 bg-red-50 px-1.5 py-0.5 rounded line-through">{entry.old_value || "(empty)"}</span>
                    <ArrowRightLeft className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">{entry.new_value}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8"><History className="w-8 h-8 mx-auto text-slate-200 mb-2" /><p className="text-sm text-slate-400">No revisions recorded</p></div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Default Rates Dialog */}
      <Dialog open={showDefaultRates} onOpenChange={setShowDefaultRates}>
        <DialogContent className="max-w-lg" data-testid="default-rates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><IndianRupee className="w-5 h-5 text-indigo-600" /> Default Task Rates</DialogTitle>
            <DialogDescription>Centralised rates used as fallback when a resource has no individual rate configured.</DialogDescription>
          </DialogHeader>

          {defaultRatesLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
          ) : (
            <div className="space-y-3 mt-2">
              {/* Existing default rates */}
              {defaultRates.length > 0 ? (
                <div className="space-y-1.5">
                  {defaultRates.map((r, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg" data-testid={`default-rate-${i}`}>
                      <Badge className="bg-blue-100 text-blue-700 text-[10px] border-0">{r.task_type}</Badge>
                      <span className="text-sm font-semibold text-slate-800 flex-1">{"\u20B9"}{r.rate}</span>
                      <Badge className="text-[9px] bg-slate-100 text-slate-500 border-0">{r.rate_type}</Badge>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => removeDefaultRate(i)} data-testid={`remove-default-rate-${i}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">No default rates configured</p>
              )}

              {/* Add new default rate */}
              <div className="border border-dashed border-slate-200 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Add Default Rate</p>
                <div className="flex items-center gap-2">
                  <Select value={newDefaultRate.task_type} onValueChange={v => setNewDefaultRate(p => ({ ...p, task_type: v }))}>
                    <SelectTrigger className="h-8 text-xs flex-1" data-testid="default-rate-task-type"><SelectValue placeholder="Task Type" /></SelectTrigger>
                    <SelectContent>
                      {allTaskTypes.filter(t => t && !defaultRates.find(r => r.task_type === t)).map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number" placeholder="Rate" value={newDefaultRate.rate}
                    onChange={e => setNewDefaultRate(p => ({ ...p, rate: e.target.value }))}
                    className="h-8 text-xs w-24" data-testid="default-rate-value"
                  />
                  <Select value={newDefaultRate.rate_type} onValueChange={v => setNewDefaultRate(p => ({ ...p, rate_type: v }))}>
                    <SelectTrigger className="h-8 text-xs w-28" data-testid="default-rate-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_unit">Per Unit</SelectItem>
                      <SelectItem value="flat">Flat</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="h-8" onClick={addDefaultRate} disabled={!newDefaultRate.task_type || !newDefaultRate.rate} data-testid="add-default-rate-btn">
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Save + Apply */}
              <div className="flex items-center gap-2 pt-2">
                <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={async () => { await handleSaveDefaultRates(); }} disabled={defaultRatesSaving} data-testid="save-default-rates-btn">
                  {defaultRatesSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  {defaultRatesSaving ? "Saving..." : "Save Default Rates"}
                </Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={async () => { await handleSaveDefaultRates(); await handleApplyGlobal(); setShowDefaultRates(false); }} disabled={defaultRatesSaving || applyingGlobal} data-testid="save-and-apply-btn">
                  {applyingGlobal ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                  Save & Apply to All
                </Button>
              </div>

              <p className="text-[10px] text-slate-400 leading-relaxed">
                Individual resource rates always take priority. Default rates only apply to tasks where the resource has no matching individual rate.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceCenter;
